/**
 * Issues in a result's review (specification §10): what the brand and creators raise, and how
 * the admin team decides.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.js";
import type { PublicCampaign } from "./campaigns.js";

/** An issue, as the API describes it. */
export interface Issue {
  id: number;
  raisedBy: string;
  raisedAt: string;
  /** The item it is about. Null for the whole campaign. */
  submissionId: number | null;
  summary: string;
  /** Null while open. */
  outcome: "upheld" | "rejected" | null;
  decision: string | null;
  decidedAt: string | null;
}

/** An issue as `GET /api/admin/issues` describes it, with its campaign and item. */
export interface AdminIssue extends Issue {
  decidedBy: string | null;
  campaign: {
    chainId: number;
    escrow: string;
    id: string;
    title: string;
    platform: "X" | "YOUTUBE";
    brand: string;
    status: string;
    endAt: string;
    /** A correction reaches the settlement only through a revision published by then. */
    lastRevisionBy: string;
  };
  item: {
    submissionId: number;
    url: string;
    contentId: string | null;
    wallet: string;
    status: string;
  } | null;
}

/** The checks a correction can change, and the reason a failure takes. */
export const CORRECTABLE_CHECKS = {
  edits: "Edits (EDITED)",
  rules: "Rules (DUPLICATE)",
  elements: "Required elements (MISSING_ELEMENT)",
  media: "Media match (MEDIA_MISMATCH)",
  format: "Format (NOT_DEDICATED or PROMOTION_NOT_IN_SEGMENT, by the campaign's format)",
  manipulation: "Manipulation (MANIPULATION)",
  semantic: "Brief requirements (BRIEF_NOT_MET)",
  visual: "Visual requirements (BRIEF_NOT_MET)",
  fraud: "Fraud (FRAUD)",
} as const;

export type CorrectableCheck = keyof typeof CORRECTABLE_CHECKS;

const issuesPath = (campaign: Pick<PublicCampaign, "escrow" | "id">) =>
  `/api/campaigns/${campaign.escrow}/${campaign.id}/issues`;

/** The signed-in wallet's issues in a campaign. */
export function useMyIssues(campaign: Pick<PublicCampaign, "escrow" | "id">, enabled: boolean) {
  return useQuery({
    queryKey: ["issues", campaign.escrow, campaign.id],
    queryFn: async () => (await api.get<{ issues: Issue[] }>(issuesPath(campaign))).issues,
    enabled,
  });
}

/** Raises an issue in a campaign's review. */
export function useRaiseIssue(campaign: Pick<PublicCampaign, "escrow" | "id">) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { summary: string; submissionId?: number }) =>
      api.post<Issue>(issuesPath(campaign), input),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["issues", campaign.escrow, campaign.id] }),
  });
}

const ADMIN_KEY = ["admin-issues"] as const;

/** Issues for the admin team: open ones, or decided ones. */
export function useAdminIssues(status: "open" | "decided") {
  return useQuery({
    queryKey: [...ADMIN_KEY, status],
    queryFn: async () =>
      (await api.get<{ issues: AdminIssue[] }>(`/api/admin/issues?status=${status}`)).issues,
  });
}

/** Decides an issue, possibly correcting a check of an item. */
export function useDecideIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      outcome: "upheld" | "rejected";
      decision: string;
      correction?: { submissionId: number; check: CorrectableCheck; outcome: "pass" | "fail" };
    }) => {
      const { id, ...body } = input;
      return api.post(`/api/admin/issues/${id}/decide`, body);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ADMIN_KEY }),
  });
}
